import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TabContent,
  Upload,
  ButtonIcon,
  Icon,
  Loader,
  useUploadFile,
} from '@saviynt/design-system';
import { useDispatch } from 'react-redux';
import { checkEnvironment } from 'ui-common/src/lib/utils';
import { message } from '../../utils/helper';
import { uploadAsFile } from '../../../../redux/adminHome-actions';
import LinkButton from '../LinkButton/LinkButton';
import styles from './BrandingTabs.module.scss';

const UPLOAD_TARGET_PATH = '/app';

/**
 * Checks whether a file's extension matches the allowed acceptTypes string.
 * @param {File} file - The file to validate.
 * @param {string} acceptTypes - Comma-separated accepted extensions, e.g. ".jpg" or ".jpg,.png".
 * @returns {boolean} True if the file extension is allowed.
 */
const isValidFileType = (file, acceptTypes) => {
  const allowed = acceptTypes.split(',').map((ext) => ext.trim().toLowerCase().replace(/^\./, ''));
  const fileExt = file.name.split('.').pop()?.toLowerCase();

  return allowed.includes(fileExt);
};

const BYTES_PER_MB = 1024 * 1024;
const MAX_FILE_SIZE_MB = 2;

/**
 * Checks whether a file is within the allowed size limit.
 * @param {File} file - The file to validate.
 * @returns {boolean} True if the file size is within the limit.
 */
const isValidFileSize = (file) => file.size <= MAX_FILE_SIZE_MB * BYTES_PER_MB;

const BG_FILENAMES = Array.from({ length: 9 }, (_, i) => `bg${i + 1}.jpg`);
const CUSTOM_FILENAMES = Array.from({ length: 5 }, (_, i) => `custom${i + 1}.png`);

const MAX_BG = 9;
const MAX_EMAIL = 5;

/**
 * Images tab content for BrandingConfig — login page image,
 * and email image upload sections.
 */
const ImagesTab = ({ intl, activeTab, setShowExampleModalVariant }) => {
  // Each entry: { fileName, previewUrl }
  const [bgImages, setBgImages] = useState([]);
  const [emailImages, setEmailImages] = useState([]);

  // Track which index is in "edit" (replace) mode per section
  const [editingBgIndex, setEditingBgIndex] = useState(null);
  const [editingEmailIndex, setEditingEmailIndex] = useState(null);

  // Loading states for initial image fetches
  const [isBgLoading, setIsBgLoading] = useState(true);
  const [isEmailLoading, setIsEmailLoading] = useState(true);

  const dispatch = useDispatch();

  // File validation for login page images: 2 MB per file, up to 9 files
  const {
    handleFileSelect: handleBgFileSelect,
    handleFileDelete: handleBgFileDelete,
    uploadedFiles: bgUploadedFiles,
    maxFileSize: bgMaxFileSize,
    maxTotalFileSize: bgMaxTotalFileSize,
    maxFileCount: bgMaxFileCount,
  } = useUploadFile(MAX_BG * 2, MAX_BG, 2);

  // File validation for email images: 2 MB per file, up to 5 files
  const {
    handleFileSelect: handleEmailFileSelect,
    handleFileDelete: handleEmailFileDelete,
    uploadedFiles: emailUploadedFiles,
    maxFileSize: emailMaxFileSize,
    maxTotalFileSize: emailMaxTotalFileSize,
    maxFileCount: emailMaxFileCount,
  } = useUploadFile(MAX_EMAIL * 2, MAX_EMAIL, 2);

  const IMAGE_UPLOAD_URL = `${checkEnvironment().IDWMS_API_URL}/image/uploadAsFile`;
  const IMAGE_GET_URL = `${checkEnvironment().IDWMS_API_URL}/image/getAsFile`;

  const getMimeType = (name) => {
    const ext = name.split('.').pop()?.toLowerCase();
    const mimeMap = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
    };
    return mimeMap[ext] || 'application/octet-stream';
  };

  const fetchExistingImage = async (fileName) => {
    try {
      const url = `${IMAGE_GET_URL}?path=${UPLOAD_TARGET_PATH}/${fileName}`;
      // Suppress global error toast for 404s — missing images are expected
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-NOERRORMODAL': 'true' },
      });

      if (response.status === 200) {
        const rawBlob = await response.blob();
        const correctType = getMimeType(fileName);
        const typedBlob = new Blob([rawBlob], { type: correctType });
        const previewUrl = URL.createObjectURL(typedBlob);
        return { fileName, previewUrl };
      }
    } catch (err) {
      // silently skip missing images
    }
    return null;
  };

  useEffect(() => {
    const loadExistingImages = async () => {
      // Fetch bg images; stop loading on any response (success or error)
      try {
        const bgResults = await Promise.all(BG_FILENAMES.map(fetchExistingImage));
        setBgImages(bgResults.filter(Boolean));
      } finally {
        setIsBgLoading(false);
      }

      // Fetch email images; stop loading on any response (success or error)
      try {
        const customResults = await Promise.all(CUSTOM_FILENAMES.map(fetchExistingImage));
        setEmailImages(customResults.filter(Boolean));
      } finally {
        setIsEmailLoading(false);
      }
    };

    loadExistingImages();

    return () => {
      // Revoke blob URLs on unmount
      bgImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      emailImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, []);

  /**
   * Upload a file to the backend with a specific target filename,
   * then update the local state with the new preview.
   */
  const uploadAndReplace = (file, targetFileName, setImages, index) => {
    const renamedFile = new File([file], targetFileName, { type: file.type });
    const newPreviewUrl = URL.createObjectURL(file);

    dispatch(
      uploadAsFile(
        IMAGE_UPLOAD_URL,
        renamedFile,
        UPLOAD_TARGET_PATH,
        () => {
          setImages((prev) => {
            const updated = [...prev];
            // Revoke old blob if replacing
            if (updated[index]?.previewUrl) {
              URL.revokeObjectURL(updated[index].previewUrl);
            }
            updated[index] = { fileName: targetFileName, previewUrl: newPreviewUrl };
            return updated;
          });
        },
        () => {
          URL.revokeObjectURL(newPreviewUrl);
        }
      )
    );
  };

  /**
   * Upload a new file appended to the end of a section's list.
   */
  const uploadNew = (file, prefix, ext, currentImages, setImages) => {
    const nextIndex = currentImages.length;
    const targetFileName = `${prefix}${nextIndex + 1}${ext}`;
    const renamedFile = new File([file], targetFileName, { type: file.type });
    const newPreviewUrl = URL.createObjectURL(file);

    dispatch(
      uploadAsFile(
        IMAGE_UPLOAD_URL,
        renamedFile,
        UPLOAD_TARGET_PATH,
        () => {
          setImages((prev) => [...prev, { fileName: targetFileName, previewUrl: newPreviewUrl }]);
        },
        () => {
          URL.revokeObjectURL(newPreviewUrl);
        }
      )
    );
  };

  /**
   * Renders a single image item — either a preview container with edit button,
   * or an Upload dropzone if the user clicked edit on this item.
   */
  const renderImageItem = (
    img,
    index,
    editingIndex,
    setEditingIndex,
    setImages,
    acceptTypes,
    previewClassName,
    uploadProps
  ) => {
    const isEditing = editingIndex === index;

    if (isEditing) {
      return (
        <Box key={img.fileName} className={styles.imageItemEditing}>
          <Upload
            kind="dropzone"
            isMultiple={false}
            onFileSelect={(files) => {
              if (files[0] && isValidFileType(files[0], acceptTypes) && isValidFileSize(files[0])) {
                uploadAndReplace(files[0], img.fileName, setImages, index);
                setEditingIndex(null);
              }
            }}
            onFileDelete={() => {}}
            uploadedFiles={[]}
            acceptTypes={acceptTypes}
            maxFileSize={uploadProps.maxFileSize}
            maxFileCount={1}
            maxTotalFileSize={uploadProps.maxFileSize}
            isReuploadWithDelete={false}
            showHeader={false}
            className={styles.uploadFullWidth}
          />
          <ButtonIcon
            size="medium"
            kind="outlinedSecondary"
            icon={<Icon kind="ChevronLeft" />}
            onClick={() => setEditingIndex(null)}
            className={styles.uploadCloseButton}
          />
        </Box>
      );
    }

    return (
      <Box key={img.fileName} className={styles.imageItemContainer}>
        <Box className={styles.imageItemHeader}>
          <span className={styles.imageItemFileName}>{img.fileName}</span>
          <ButtonIcon
            size="medium"
            kind="outlinedSecondary"
            icon={<Icon kind="Edit" />}
            onClick={() => setEditingIndex(index)}
          />
        </Box>
        <Box
          className={`${styles.imageItemPreview}${previewClassName ? ` ${previewClassName}` : ''}`}
        >
          <img src={img.previewUrl} alt={img.fileName} />
        </Box>
      </Box>
    );
  };

  const getHeaderSection = (heading, subHeading, seeExampleOnClick) => (
    <Box className={styles.sectionHeader}>
      <Typography kind="h3">{heading}</Typography>
      <Typography kind="body2" color="--color-foreground-neutral-subtle">
        {subHeading}
        {seeExampleOnClick && (
          <>
            {' '}
            <LinkButton
              onClick={seeExampleOnClick}
              label={message(intl, 'UI.Gen2.Admin.Branding.Button.SeeExample')}
            />
          </>
        )}
      </Typography>
    </Box>
  );

  return (
    <TabContent value={activeTab} tabValue="bc_tab_images">
      {/* Login Page Image (up to 9) */}
      <Box className={styles.sectionWrapper}>
        {getHeaderSection(
          message(intl, 'UI.Gen2.Admin.Branding.Images.LoginPageImage.Header'),
          message(intl, 'UI.Gen2.Admin.Branding.Images.LoginPageImage.Description'),
          () => setShowExampleModalVariant('loginPageImage')
        )}
        <Box className={styles.sectionContent}>
          {isBgLoading ? (
            <Loader kind="dots" format="inline" color="brand" />
          ) : (
            <>
              {bgImages.map((img, i) =>
                renderImageItem(
                  img,
                  i,
                  editingBgIndex,
                  setEditingBgIndex,
                  setBgImages,
                  '.jpg',
                  undefined,
                  {
                    maxFileSize: bgMaxFileSize,
                    maxFileCount: bgMaxFileCount,
                    maxTotalFileSize: bgMaxTotalFileSize,
                  }
                )
              )}
              {bgImages.length < MAX_BG && editingBgIndex === null && (
                <Upload
                  kind="dropzone"
                  isMultiple={false}
                  onFileSelect={(files) => {
                    if (
                      files[0] &&
                      isValidFileType(files[0], '.jpg') &&
                      isValidFileSize(files[0])
                    ) {
                      handleBgFileSelect(files);
                      uploadNew(files[0], 'bg', '.jpg', bgImages, setBgImages);
                    }
                  }}
                  onFileDelete={handleBgFileDelete}
                  uploadedFiles={bgUploadedFiles}
                  acceptTypes=".jpg"
                  maxFileSize={bgMaxFileSize}
                  maxFileCount={bgMaxFileCount}
                  maxTotalFileSize={bgMaxTotalFileSize}
                  isReuploadWithDelete={false}
                  showHeader={false}
                />
              )}
            </>
          )}
        </Box>
      </Box>
      <Box className={styles.divider} />
      {/* Email Image (up to 5) */}
      <Box className={styles.sectionWrapper}>
        {getHeaderSection(
          message(intl, 'UI.Gen2.Admin.Branding.Images.EmailImage.Header'),
          message(intl, 'UI.Gen2.Admin.Branding.Images.EmailImage.Description')
        )}
        <Box className={styles.sectionContent}>
          {isEmailLoading ? (
            <Loader kind="dots" format="inline" color="brand" />
          ) : (
            <>
              {emailImages.map((img, i) =>
                renderImageItem(
                  img,
                  i,
                  editingEmailIndex,
                  setEditingEmailIndex,
                  setEmailImages,
                  '.png',
                  styles['imageItemPreview--contain'],
                  {
                    maxFileSize: emailMaxFileSize,
                    maxFileCount: emailMaxFileCount,
                    maxTotalFileSize: emailMaxTotalFileSize,
                  }
                )
              )}
              {emailImages.length < MAX_EMAIL && editingEmailIndex === null && (
                <Upload
                  kind="dropzone"
                  isMultiple={false}
                  onFileSelect={(files) => {
                    if (
                      files[0] &&
                      isValidFileType(files[0], '.png') &&
                      isValidFileSize(files[0])
                    ) {
                      handleEmailFileSelect(files);
                      uploadNew(files[0], 'custom', '.png', emailImages, setEmailImages);
                    }
                  }}
                  onFileDelete={handleEmailFileDelete}
                  uploadedFiles={emailUploadedFiles}
                  acceptTypes=".png"
                  maxFileSize={emailMaxFileSize}
                  maxFileCount={emailMaxFileCount}
                  maxTotalFileSize={emailMaxTotalFileSize}
                  isReuploadWithDelete={false}
                  showHeader={false}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </TabContent>
  );
};

export default ImagesTab;
